import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';

import ControlBar from './components/ControlBar';
import ChatPanel, { ChatMessage } from './components/ChatPanel';
import ReportModal from './components/ReportModal';
import ConnectionQuality from './components/ConnectionQuality';
import UserBadge from './components/UserBadge';
import ChatStatus, { ChatConnectionStatus } from './components/ChatStatus';
import ToastContainer, { useToast } from '@/components/base/ToastContainer';

type ModalType = 'report' | 'block' | null;

const VideoChatPage = () => {
  const navigate = useNavigate();
  const { toasts, addToast, removeToast } = useToast();

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  const socketRef = useRef<any>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);

  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [modal, setModal] = useState<ModalType>(null);
  const [connectionStatus, setConnectionStatus] = useState<ChatConnectionStatus>('connecting');
  const [quality, setQuality] = useState<'excellent' | 'good' | 'poor'>('good');
  const [duration, setDuration] = useState(0);
  const [isSkipping, setIsSkipping] = useState(false);
  const [selfBlur] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  const [isMatched, setIsMatched] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const currentRemote = {
    flag: '🌐',
    country: 'Canlı Eşleşme',
    countryCode: 'LIVE',
    isPremium: false,
  };

  const rtcConfig: RTCConfiguration = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      {
        urls: 'turn:chatnow.smarthizmet.com:3478',
        username: 'test',
        credential: 'test123',
      },
    ],
  };

  useEffect(() => {
    if (connectionStatus !== 'connected') return;

    const interval = setInterval(() => {
      setDuration((d) => d + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [connectionStatus]);

  useEffect(() => {
    const handleFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const cleanupPeer = useCallback(() => {
    if (peerRef.current) {
      try {
        peerRef.current.ontrack = null;
        peerRef.current.onicecandidate = null;
        peerRef.current.onconnectionstatechange = null;
        peerRef.current.close();
      } catch (error) {
        console.error(error);
      }
      peerRef.current = null;
    }

    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }

    setIsMatched(false);
    setDuration(0);
  }, []);

  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection(rtcConfig);

    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit('ice-candidate', event.candidate);
      }
    };

    pc.ontrack = (event) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
        remoteVideoRef.current.play().catch(() => {});
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;

      if (state === 'connected') {
        setConnectionStatus('connected');
        setQuality('excellent');
      } else if (state === 'connecting') {
        setConnectionStatus('connecting');
        setQuality('good');
      } else if (state === 'disconnected' || state === 'failed') {
        setConnectionStatus('disconnected');
        setQuality('poor');
      } else if (state === 'closed') {
        setConnectionStatus('disconnected');
      }
    };

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current as MediaStream);
      });
    }

    peerRef.current = pc;
    return pc;
  }, []);

  const startLocalCamera = useCallback(async () => {
    try {
      if (localStreamRef.current) {
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
          localVideoRef.current.play().catch(() => {});
        }
        setMediaReady(true);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      localStreamRef.current = stream;

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        localVideoRef.current.play().catch(() => {});
      }

      setCameraOn(true);
      setMicOn(true);
      setMediaReady(true);
      addToast('Kamera ve mikrofon hazır', 'success');
    } catch (error) {
      console.error(error);
      setMediaReady(false);
      setCameraOn(false);
      setMicOn(false);
      setConnectionStatus('disconnected');
      addToast('Kamera veya mikrofon izni alınamadı', 'error');
    }
  }, [addToast]);

  const requestMatch = useCallback(() => {
    if (!socketRef.current || !mediaReady) return;
    setConnectionStatus('connecting');
    socketRef.current.emit('start');
  }, [mediaReady]);

  const makeOffer = useCallback(async () => {
    try {
      if (!localStreamRef.current) return;

      const pc = createPeerConnection();
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      if (socketRef.current) {
        socketRef.current.emit('offer', offer);
      }
    } catch (error) {
      console.error(error);
    }
  }, [createPeerConnection]);

  useEffect(() => {
    startLocalCamera();

    return () => {
      cleanupPeer();

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }
    };
  }, [startLocalCamera, cleanupPeer]);

  useEffect(() => {
    const socket = io('https://chatnow.smarthizmet.com', {
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
      setConnectionStatus('connecting');
      console.log('socket bağlandı', socket.id);
    });

    socket.on('disconnect', () => {
      setSocketConnected(false);
      setConnectionStatus('disconnected');
      cleanupPeer();
    });

    socket.on('waiting', () => {
      setConnectionStatus('connecting');
    });

    socket.on('matched', async (data: { partnerId: string }) => {
      setIsMatched(true);
      setConnectionStatus('connecting');
      setDuration(0);
	  setMessages([]);

      if (socket.id > data.partnerId) {
        setTimeout(async () => {
          if (!peerRef.current) {
            await makeOffer();
          }
        }, 500);
      }
    });

    socket.on('partner_left', () => {
      cleanupPeer();
      setConnectionStatus('disconnected');
      addToast('Karşı taraf ayrıldı', 'info');
	  setMessages([]);
    });

    socket.on('offer', async (offer: RTCSessionDescriptionInit) => {
      try {
        if (!localStreamRef.current) {
          await startLocalCamera();
        }

        const pc = peerRef.current || createPeerConnection();

        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socket.emit('answer', answer);
      } catch (error) {
        console.error('Offer işlenemedi:', error);
      }
    });

    socket.on('answer', async (answer: RTCSessionDescriptionInit) => {
      try {
        if (peerRef.current) {
          await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));
        }
      } catch (error) {
        console.error('Answer işlenemedi:', error);
      }
    });

    socket.on('ice-candidate', async (candidate: RTCIceCandidateInit) => {
      try {
        if (peerRef.current) {
          await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        }
      } catch (error) {
        console.error('ICE candidate eklenemedi:', error);
      }
    });
	
	socket.on('chat-message', (data: { text: string }) => {
  const incomingMessage: ChatMessage = {
    id: Date.now() + Math.floor(Math.random() * 1000),
    text: data.text,
    sender: 'them',
    time: getCurrentTime(),
  };

  setMessages((prev) => [...prev, incomingMessage]);
});

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [addToast, cleanupPeer, createPeerConnection, makeOffer, startLocalCamera]);

  useEffect(() => {
    if (socketConnected && mediaReady) {
      requestMatch();
    }
  }, [socketConnected, mediaReady, requestMatch]);
  
  const getCurrentTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

  const handleNext = useCallback(() => {
    setIsSkipping(true);
    cleanupPeer();
    setConnectionStatus('connecting');
    setChatOpen(false);
	setMessages([]);

    if (socketRef.current) {
      socketRef.current.emit('next');
    }

    setTimeout(() => {
      setIsSkipping(false);
      addToast('Yeni eşleşme aranıyor...', 'info');
    }, 800);
  }, [addToast, cleanupPeer]);

  const handleStop = useCallback(() => {
    cleanupPeer();

    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    navigate('/');
  }, [cleanupPeer, navigate]);

  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  const handleMicToggle = () => {
    const nextMicState = !micOn;
    setMicOn(nextMicState);

    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = nextMicState;
      });
    }

    addToast(nextMicState ? 'Mikrofon açıldı' : 'Mikrofon kapatıldı', 'info');
  };

  const handleCameraToggle = () => {
    const nextCameraState = !cameraOn;
    setCameraOn(nextCameraState);

    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach((track) => {
        track.enabled = nextCameraState;
      });
    }

    addToast(nextCameraState ? 'Kamera açıldı' : 'Kamera kapatıldı', 'info');
  };
  
  const handleSendMessage = useCallback((text: string) => {
  if (!socketRef.current || !isMatched) {
    addToast('Aktif eşleşme yok', 'error');
    return;
  }

  const newMessage: ChatMessage = {
    id: Date.now(),
    text,
    sender: 'me',
    time: getCurrentTime(),
  };

  setMessages((prev) => [...prev, newMessage]);
  socketRef.current.emit('chat-message', { text });
}, [addToast, isMatched]);

  const handleReportConfirm = (reason?: string) => {
    setModal(null);
    addToast(reason ? `Rapor gönderildi: ${reason}` : 'Kullanıcı engellendi', 'success');
    setTimeout(() => handleNext(), 600);
  };

  return (
    <div className={`relative w-full bg-black overflow-hidden ${fullscreen ? 'h-screen' : 'min-h-screen'}`}>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="absolute inset-0 z-0 bg-black">
        {isSkipping ? (
          <div className="w-full h-full bg-black flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="relative w-20 h-20 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full border-2 border-cyan-500/30 animate-spin" style={{ animationDuration: '1s' }} />
                <i className="ri-skip-forward-line text-cyan-400 text-3xl" />
              </div>
              <p className="text-white/50 text-sm">Sonraki kullanıcı aranıyor...</p>
            </div>
          </div>
        ) : (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className={`w-full h-full object-cover object-top transition-all duration-700 ${
              connectionStatus === 'connecting' ? 'blur-xl scale-105 brightness-50' : 'blur-0 scale-100 brightness-75'
            }`}
          />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20" />
      </div>

      <div className="absolute top-0 left-0 right-0 z-10 px-4 md:px-6 pt-4 md:pt-5 flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/0758b00e-4705-432c-90d8-8173dccd9e57.png"
              alt="ChatNow"
              className="h-8 w-auto object-contain opacity-80"
            />
          </div>
          <ChatStatus status={connectionStatus} />
        </div>

        {isMatched && connectionStatus === 'connected' && (
          <div className="flex-1 flex justify-center mt-1">
            <UserBadge
              country={currentRemote.country}
              countryCode={currentRemote.countryCode}
              flag={currentRemote.flag}
              isPremium={currentRemote.isPremium}
              duration={duration}
            />
          </div>
        )}

        <div className="flex items-center gap-3 mt-1">
          <div className="px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/10">
            <ConnectionQuality quality={socketConnected ? quality : 'disconnected'} />
          </div>

          {!chatOpen && (
            <button
              onClick={() => setChatOpen(true)}
              className="relative w-9 h-9 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white/60 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-chat-3-line text-base" />
            </button>
          )}
        </div>
      </div>

      <div
        className={`absolute z-10 transition-all duration-300 ${
          chatOpen ? 'bottom-24 right-[336px]' : 'bottom-24 right-4 md:right-6'
        }`}
      >
        <div
          className="relative rounded-2xl overflow-hidden border border-white/15 shadow-2xl bg-[#0d1117]"
          style={{ width: '140px', height: '190px' }}
        >
          {cameraOn ? (
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className={`w-full h-full object-cover object-top ${selfBlur ? 'blur-md' : ''}`}
            />
          ) : (
            <div className="w-full h-full bg-[#0d1117] flex flex-col items-center justify-center gap-2">
              <i className="ri-camera-off-line text-white/25 text-2xl" />
              <span className="text-white/25 text-xs">Kamera kapalı</span>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
          <div className="absolute bottom-2 left-2 text-white/60 text-xs font-medium">Sen</div>

          {!micOn && (
            <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-500/90">
              <i className="ri-mic-off-line text-white text-xs" />
            </div>
          )}
        </div>
      </div>

      {connectionStatus === 'connecting' && !isSkipping && (
        <div className="absolute inset-0 z-5 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-24 h-24 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-2 border-cyan-500/20 animate-spin" style={{ animationDuration: '2s' }} />
              <div className="absolute inset-2 rounded-full border-2 border-violet-500/15 animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }} />
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500/30 to-violet-500/30 flex items-center justify-center">
                <i className="ri-user-line text-white/60 text-xl" />
              </div>
            </div>
            <div className="text-center">
              <p className="text-white/70 text-sm font-medium">Bağlanıyor...</p>
              <p className="text-white/35 text-xs mt-1">Canlı eşleşme aranıyor</p>
            </div>
          </div>
        </div>
      )}

   <ChatPanel
  isOpen={chatOpen}
  onClose={() => setChatOpen(false)}
  messages={messages}
  onSendMessage={handleSendMessage}
/>

      <div className="absolute bottom-0 left-0 right-0 z-10">
        <ControlBar
          micOn={micOn}
          cameraOn={cameraOn}
          chatOpen={chatOpen}
          fullscreen={fullscreen}
          onMicToggle={handleMicToggle}
          onCameraToggle={handleCameraToggle}
          onChatToggle={() => setChatOpen((v) => !v)}
          onFullscreen={handleFullscreen}
          onNext={handleNext}
          onStop={handleStop}
          onReport={() => setModal('report')}
          onBlock={() => setModal('block')}
        />
      </div>

      {modal && (
        <ReportModal
          type={modal}
          onConfirm={handleReportConfirm}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default VideoChatPage;